<script lang="ts" setup>
import { computed } from 'vue';

import { useDataTestid } from '@/kasten-components/composables';

import { injectTableContext, isInsideScrollWrapperContext } from './model';

const { isSmall } = injectTableContext();
const isInsideScrollWrapper = isInsideScrollWrapperContext();

export type KaTableCellProps = {
  /**
   * When set, wraps the cell content in a stretched `<a>` link that covers the entire row.
   * Use for cells where clicking should navigate to a different page.
   */
  linkTo?: string;
  /**
   * Accessible label for the stretched link or button.
   * When set without `linkTo`, wraps the cell content in a stretched `<button>`.
   * Use for cells where clicking performs a same-page interaction (e.g., opening a panel).
   */
  activateLabel?: string;
  /**
   * When true, positions this cell above any stretched links/buttons in the row.
   * Use on cells containing interactive elements (menus, checkboxes, links) that must remain clickable.
   */
  interactive?: boolean;
  /**
   * When set, adds a `data-testid` attribute to the stretched `<a>` or `<button>` element.
   */
  dataTestid?: string;
};

const props = defineProps<KaTableCellProps>();

const emit = defineEmits<{
  activate: [];
}>();

const cellPadding = isSmall ? 'p-2.5 md:px-3.5 md:py-2.5' : 'p-3 md:px-4 md:py-3';

const { suffix } = useDataTestid(computed(() => props.dataTestid));
</script>

<template>
  <td
    role="gridcell"
    :data-testid="dataTestid"
    :class="[
      'border-gray-200 align-middle text-sm dark:border-navy-600',
      !isInsideScrollWrapper && [
        'first:border-l last:border-r',
        'group-last/row:border-b group-last/row:first:rounded-bl-lg group-last/row:last:rounded-br-lg',
      ],
      cellPadding,
      interactive && 'relative z-10',
    ]"
  >
    <a
      v-if="linkTo"
      :href="linkTo"
      :aria-label="activateLabel"
      :data-testid="suffix('link')"
      class="flex max-w-full items-center gap-3 text-left text-inherit no-underline after:absolute after:inset-0 after:content-['']"
    >
      <slot />
    </a>
    <button
      type="button"
      v-else-if="activateLabel"
      :aria-label="activateLabel"
      :data-testid="suffix('button')"
      class="flex max-w-full items-center gap-3 text-left after:absolute after:inset-0 after:content-['']"
      @click="emit('activate')"
    >
      <slot />
    </button>
    <slot v-else />
  </td>
</template>
